import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiGet } from '../lib/api';

interface TraitantCol {
  id_user: number;
  nom_user: string;
}

interface Cell {
  id_user: number;
  total_min: number;
}

interface InterneRow {
  id_client: number;
  nom_client: string;
  cells: Cell[];
}

interface InternesResponse {
  mois: string;
  traitants: TraitantCol[];
  rows: InterneRow[];
  totals_by_traitant: Cell[];
  /** Projets hors TMA (retour 6) : même forme, à part (absent d'une API plus ancienne). */
  hors_tma?: { rows: InterneRow[]; totals_by_traitant: Cell[] };
}

function fmtMin(min: number): string {
  if (min <= 0) return '—';
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  return `${h} H ${m.toString().padStart(2, '0')}`;
}

function moisOptions(): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    out.push({ value: ym, label });
  }
  return out;
}

export function StatsInternesPage() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState<InternesResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const mois = params.get('mois') ?? '';
  const opts = useMemo(moisOptions, []);

  useEffect(() => {
    setLoading(true);
    setErr(null);
    const url = mois ? `/api/stats/internes?mois=${mois}` : '/api/stats/internes';
    apiGet<InternesResponse>(url)
      .then(setData)
      .catch((e) => setErr(e instanceof Error ? e.message : 'Erreur'))
      .finally(() => setLoading(false));
  }, [mois]);

  function changeMois(v: string) {
    if (v === '') params.delete('mois');
    else params.set('mois', v);
    setParams(params);
  }

  return (
    <div style={{ maxWidth: 1400 }}>
      <div style={{ marginBottom: 12 }}>
        <Link to="/" style={{ fontSize: 'var(--fs-xs)' }}>
          ← retour
        </Link>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <h1 style={{ fontSize: 'var(--fs-xl)', fontWeight: 600, margin: 0 }}>📅 Temps passé interne</h1>
        <select
          value={mois}
          onChange={(e) => changeMois(e.target.value)}
          style={{ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: 4, fontSize: 'var(--fs-sm)' }}
        >
          <option value="">Mois en cours</option>
          {opts.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {loading && <div style={{ color: 'var(--text-mute)' }}>Chargement…</div>}
      {err && <div style={{ color: 'var(--danger)' }}>{err}</div>}

      {data && data.rows.length === 0 && (
        <div style={{ color: 'var(--text-mute)', fontSize: 'var(--fs-sm)' }}>Aucune saisie de temps TMA sur ce mois.</div>
      )}

      {data && data.rows.length > 0 && <Matrix traitants={data.traitants} rows={data.rows} totals={data.totals_by_traitant} />}

      {data?.hors_tma && data.hors_tma.rows.length > 0 && (
        <>
          <h2 style={{ fontSize: 'var(--fs-md)', fontWeight: 600, margin: '24px 0 4px' }}>Projets hors TMA</h2>
          <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-mute)', marginBottom: 8 }}>
            Points marqués « hors TMA » : leur temps n’entre pas dans la TMA du client (tableau ci-dessus ni statistiques clients).
          </div>
          <Matrix traitants={data.traitants} rows={data.hors_tma.rows} totals={data.hors_tma.totals_by_traitant} />
        </>
      )}
    </div>
  );
}

function Matrix({ traitants, rows, totals }: { traitants: TraitantCol[]; rows: InterneRow[]; totals: Cell[] }) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 'var(--fs-xs)',
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          minWidth: 700,
        }}
      >
        <thead>
          <tr style={{ background: 'var(--bg-soft)' }}>
            <th style={th()}>Client</th>
            {traitants.map((t) => (
              <th key={t.id_user} style={th('right')}>
                {t.nom_user.split(/\s+/).map((w, i) => (
                  <span key={i} style={{ display: 'block' }}>
                    {w}
                  </span>
                ))}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id_client} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={{ ...td(), fontWeight: 600 }}>{r.nom_client}</td>
              {r.cells.map((c) => (
                <td key={c.id_user} style={td('right')}>
                  {fmtMin(c.total_min)}
                </td>
              ))}
            </tr>
          ))}
          <tr style={{ borderTop: '2px solid var(--border)', background: 'var(--bg-soft)' }}>
            <td style={{ ...td(), fontWeight: 700 }}>TOTAL</td>
            {totals.map((c) => (
              <td key={c.id_user} style={{ ...td('right'), fontWeight: 700 }}>
                {fmtMin(c.total_min)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function th(align: 'left' | 'right' = 'left'): React.CSSProperties {
  return {
    padding: '8px 10px',
    fontSize: 'var(--fs-xs)',
    textTransform: 'uppercase',
    fontWeight: 700,
    textAlign: align,
    color: 'var(--text-mute)',
  };
}

function td(align: 'left' | 'right' = 'left'): React.CSSProperties {
  return { padding: '8px 10px', textAlign: align };
}
